import * as React from "react";
import { Alert, Button, Stack, ToggleButton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { FeedbackConfig } from "@/api/feedback";
import { requestErrorKey, useRoadmapMatch, useVote, type RoadmapMatch } from "@/api/requests";
import { FeedbackForm } from "@/features/feedback/FeedbackForm";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

/** One FW item with its "I want this too" toggle (the request form's
 *  matches and the My requests tab). */
export function RoadmapVoteRow(props: {
  item: RoadmapMatch;
  onError: (err: unknown) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const vote = useVote();
  const { item } = props;
  return (
    <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
        {t("ask.fw_label", { fw: item.fw })} · {item.title} · {t(`ask.status.${item.status}`)} ·{" "}
        {t("ask.votes", { count: item.votes })}
      </Typography>
      <ToggleButton
        size="small"
        value="vote"
        selected={item.voted}
        disabled={vote.isPending}
        onChange={() => vote.mutate({ fw: item.fw, on: !item.voted }, { onError: props.onError })}
        aria-label={t("ask.want_too_fw", { fw: item.fw })}
        sx={{ flexShrink: 0 }}
      >
        {t("ask.want_too")}
      </ToggleButton>
    </Stack>
  );
}

/** Request this (``idea``) or Report a problem (``bug``): the feedback
 *  form, pre-filled with the question. A request also lists the open FW
 *  items its text matches, so a duplicate becomes a vote. */
export function RequestForm(props: {
  kind: "idea" | "bug";
  question: string;
  config: FeedbackConfig;
  onSent: () => void;
  onBack: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [draft, setDraft] = React.useState(props.question);
  const q = useDebouncedValue(draft.trim(), 250);
  const matches = useRoadmapMatch(q, i18n.language, props.kind === "idea");
  const [error, setError] = React.useState<string | null>(null);
  const fail = (err: unknown) => setError(t(requestErrorKey(err) ?? "ask.errors.generic"));
  const items = props.kind === "idea" ? (matches.data ?? []) : [];

  return (
    <Stack sx={{ gap: 1.5 }}>
      <Button size="small" onClick={props.onBack} sx={{ alignSelf: "flex-start" }}>
        {t("ask.back")}
      </Button>
      <Typography variant="caption" color="text.secondary">
        {t("ask.private_note")}
      </Typography>
      <FeedbackForm
        config={props.config}
        category={props.kind}
        initialMessage={props.question}
        source="ask_panel"
        onSent={() => props.onSent()}
        onCancel={props.onBack}
        onMessageChange={setDraft}
      >
        {items.length > 0 && (
          <Stack component="section" sx={{ gap: 0.75 }}>
            <Typography variant="subtitle2">{t("ask.matches_heading")}</Typography>
            {items.map((item) => (
              <RoadmapVoteRow key={item.fw} item={item} onError={fail} />
            ))}
          </Stack>
        )}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </FeedbackForm>
    </Stack>
  );
}
